import { useEffect, useMemo, useState } from "react";
import PageShell from "@/components/feature/PageShell";
import ClipCard from "@/components/feature/ClipCard";
import SearchInput from "@/components/base/SearchInput";
import ChipGroup from "@/components/base/ChipGroup";
import SelectField from "@/components/base/SelectField";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { clipList, clipTypes } from "@/utils/catalog";

const PAGE_SIZE = 12;

const sortOptions = [
  { label: "默认顺序", value: "default" },
  { label: "时长由长到短", value: "longest" },
  { label: "时长由短到长", value: "shortest" },
  { label: "播放量最高", value: "hot" },
];

export default function ClipCategoryPage() {
  const [keyword, setKeyword] = useState("");
  const [type, setType] = useState("全部");
  const [sort, setSort] = useState("default");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const list = clipList.filter((clip) => {
      const matchType = type === "全部" || clip.type === type;
      const matchKeyword =
        !q || clip.title.toLowerCase().includes(q) || clip.description.toLowerCase().includes(q);
      return matchType && matchKeyword;
    });
    const sorted = [...list];
    if (sort === "longest") sorted.sort((a, b) => b.duration - a.duration);
    if (sort === "shortest") sorted.sort((a, b) => a.duration - b.duration);
    if (sort === "hot") sorted.sort((a, b) => b.views - a.views);
    return sorted;
  }, [keyword, type, sort]);

  useEffect(() => {
    setPage(1);
  }, [keyword, type, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setKeyword("");
    setType("全部");
    setSort("default");
  };

  return (
    <PageShell active="clip" crumbs={[{ label: "首页", to: "/" }, { label: "精彩片段" }]}>
      <header className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
        <h1 className="font-heading text-2xl font-semibold text-foreground-900">年轻的母亲-精彩片段</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
          汇集剧情回顾、精彩对白与幕后花絮三类片段，按类型与时长筛选，随时重温这些让人难忘的瞬间。
        </p>
      </header>

      <div className="mt-5 flex flex-col gap-4 rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <SearchInput value={keyword} onChange={setKeyword} placeholder="搜索片段标题或内容…" className="sm:max-w-xs" />
          <SelectField label="排序" value={sort} onChange={setSort} options={sortOptions} />
        </div>
        <ChipGroup label="片段类型" options={clipTypes} value={type} onChange={setType} />
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
        <span>
          共找到 <span className="text-primary-600">{filtered.length}</span> 段片段
        </span>
        {(keyword || type !== "全部") && (
          <button type="button" onClick={resetFilters} className="transition hover:text-primary-600">
            <i className="ri-refresh-line mr-1"></i>清除筛选
          </button>
        )}
      </div>

      {pageItems.length > 0 ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 md:gap-5 xl:grid-cols-3">
          {pageItems.map((clip) => (
            <ClipCard key={clip.id} clip={clip} />
          ))}
        </div>
      ) : (
        <div className="mt-4">
          <EmptyState
            title="暂无符合条件的片段"
            description="试试清除筛选，或返回首页浏览其他推荐内容。"
            actionText="清除筛选"
            onAction={resetFilters}
          />
        </div>
      )}

      <div className="mt-8">
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </PageShell>
  );
}